import { PDFDocument } from "pdf-lib";
import { api } from "./api";

const TARGET = 500 * 1024;
const MAX_EDGE = 2200;
const listeners = new Set();
export const onUploadStatus = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = (s) => listeners.forEach((fn) => fn(s));

const loadImage = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(img.src); resolve(img); };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

const toBlob = (canvas, q) => new Promise((r) => canvas.toBlob(r, "image/jpeg", q));

async function compressImage(file) {
  if (file.size <= TARGET) return file;
  const img = await loadImage(file);
  let scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
  let best = null;
  for (let pass = 0; pass < 4; pass += 1) {
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const q of [0.88, 0.8, 0.72, 0.64]) {
      const blob = await toBlob(canvas, q);
      if (!best || blob.size < best.size) best = blob;
      if (blob.size <= TARGET) { best = blob; pass = 99; break; }
    }
    scale *= 0.8;
  }
  if (!best || best.size >= file.size) return file;
  return new File([best], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
}

async function compressPdf(file) {
  const doc = await PDFDocument.load(await file.arrayBuffer(), { updateMetadata: false });
  if (doc.isEncrypted) return file;
  const bytes = await doc.save({ useObjectStreams: true });
  if (bytes.byteLength >= file.size) return file;
  return new File([bytes], file.name, { type: "application/pdf" });
}

export async function compressFile(file) {
  try {
    if (/^image\/(jpeg|png|webp)$/.test(file.type)) return await compressImage(file);
    if (file.type === "application/pdf") return await compressPdf(file);
  } catch (e) { /* fall back to original file */ }
  return file;
}

export async function uploadFile(url, file, extra = {}) {
  const optimizable = /^image\/(jpeg|png|webp)$|^application\/pdf$/.test(file.type);
  emit({ stage: "compress", pct: 0, name: file.name, optimizable });
  try {
    const small = await compressFile(file);
    const fd = new FormData();
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", small);
    const base = { stage: "upload", name: file.name, from: file.size, to: small.size, optimizable };
    emit({ ...base, pct: 0 });
    const res = await api.post(url, fd, {
      onUploadProgress: (e) => e.total && emit({ ...base, pct: Math.round((e.loaded / e.total) * 100) }),
    });
    return res;
  } finally { emit(null); }
}
